import * as Sentry from '@sentry/react';
import moment from 'moment';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';

import { TENANT_CLOSED_TEXT } from '../auth/signOutReason';
import config from '../config';
import sharedContext from '../sharedContext';

let _refreshTm = null;

// How long sign-out waits for the backend to delete the session token before it navigates away anyway.
const SIGN_OUT_REVOKE_TIMEOUT_MS = 2000;
// The sign-out request in flight (or done): a second logout() waits for it rather than navigating it away.
let _revoking = Promise.resolve();

// Best-effort: delete this session's token on the backend (POST /v2/edit/logout/, 204; dashboard_auth.openapi.yaml),
// waiting at most SIGN_OUT_REVOKE_TIMEOUT_MS. logout() navigates away only after this, since a navigation cancels a
// request in flight. Not `keepalive`: the request is cross-origin with an Authorization header, so it needs a CORS
// preflight, and browsers may refuse a keepalive request that needs one -- the token would silently survive. Every
// error is ignored, because the local sign-out happens regardless (a refused session answers 401 here, fine too).
const revokeSessionToken = async (sessionToken) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => {
            if (controller) controller.abort();
            resolve();
        }, SIGN_OUT_REVOKE_TIMEOUT_MS);
    });
    try {
        const request = fetch(`${config.apiUrl}/logout/`, {
            method: 'POST',
            cache: 'no-store',
            headers: { Authorization: `Token ${sessionToken}` },
            signal: controller ? controller.signal : undefined,
        });
        await Promise.race([request, timeout]);
    } catch (err) {
        // Offline, refused, aborted, or fetch itself threw: nothing to do.
    } finally {
        clearTimeout(timer);
    }
};

const list2map = (list, key) => {
    const m = {};
    list.forEach((e) => {
        m[e[key]] = e;
    });
    return m;
};

export const ContextStore = createContext();

export const SIGN_IN_FAILED = 'Login failed. Please check your username and/or password';
export const SIGN_IN_UNREACHABLE = 'Could not reach the server. Check your connection and try again.';

// A throttled sign-in (429, `retry_after` seconds): "Too many attempts, try again in N minutes".
export const tooManyAttempts = (retryAfter) => {
    if (!Number.isFinite(retryAfter) || retryAfter <= 0) {
        return 'Too many attempts, try again later';
    }
    const minutes = Math.max(1, Math.ceil(retryAfter / 60));
    return `Too many attempts, try again in ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

// True when a `storage` event means ANOTHER tab signed this user out (bootstrap removed,
// or localStorage cleared) or signed in as someone else. Token rotation rewrites bootstrap
// for the same user and must not count.
export const isBootstrapSignOutElsewhere = (event, currentUserId) => {
    if (currentUserId == null) return false;
    if (event.key === null) return true; // localStorage.clear()
    if (event.key !== 'bootstrap') return false;
    if (event.newValue === null) return true;
    try {
        return JSON.parse(event.newValue)?.user?.id !== currentUserId;
    } catch (e) {
        return false;
    }
};

// True when a `storage` event means ANOTHER tab gave this user's session a new token: the Sign-in security
// page swaps it after a confirm (the backend deletes the old one). This tab must adopt it, or its next request
// answers 401 and its sign-out would end every tab's session.
export const isSessionTokenRotatedElsewhere = (event, currentUserId, currentToken) => {
    if (currentUserId == null || event.key !== 'bootstrap' || !event.newValue) return false;
    try {
        const next = JSON.parse(event.newValue);
        return next?.user?.id === currentUserId && !!next.token && next.token !== currentToken;
    } catch (e) {
        return false;
    }
};

const colorList = [
    '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080', '#ffffff', '#000000'
];

export const ContextStoreProvider = ({ children }) => {
    let bootstrap = sharedContext.get('bootstrap');
    const user = bootstrap?.user;
    const auth_method = bootstrap?.auth_method;
    const tourOperator = bootstrap?.tour_operator;
    const settings = bootstrap?.settings;
    const token = bootstrap?.token;
    const countries = bootstrap?.countries;
    const currencies = bootstrap?.currencies;
    const roles = bootstrap?.roles;
    const timelineTypes = bootstrap?.timeline_types && bootstrap.timeline_types.length > 0 ? list2map(bootstrap.timeline_types, 'id') : {};
    // const loading = null;
    // const error = null;
    // const errorMessage = null;

    // Every login payload carries tour_operator.id. A session cached without it (see
    // updateTourOperator) cannot address its own operator, so it signs in again instead.
    const isAuthenticated = bootstrap && bootstrap.token && bootstrap.user && Number.isInteger(bootstrap.tour_operator?.id) && bootstrap.settings;
    const tokenExpires = bootstrap?.token_expires;

    // Franchise state
    const franchiseData = bootstrap?.franchise;
    const isFranchise = !!franchiseData?.is_franchise;

    // The Developer section (backend metaplan S7b; apiv2/developer.openapi.yaml): the tenant is
    // `production` or `sandbox`. Null on a session stored before the backend sent it -- read as
    // production (no sandbox-only pages) until a Developer page or the next sign-in refreshes it.
    const environment = bootstrap?.environment || null;
    const productionKeyBanner = bootstrap?.production_key_banner || [];
    // The other environment (DSEC D3): a sandbox's production (the banner's "Back to production"), production's
    // sandbox (the header switch). Null when there is none, or on a session stored before the backend sent it.
    const pairedEnvironment = bootstrap?.paired || null;
    const licensees = franchiseData?.licensees || [];
    const userLicenseeIds = franchiseData?.user_licensee_ids || [];

    // Restore selected licensee from localStorage; default to first assigned licensee
    const getInitialLicensee = () => {
        const saved = sharedContext.get('selected_licensee_id');
        if (saved && licensees.some((l) => l.id === saved)) {
            return saved;
        }
        if (userLicenseeIds.length === 1) {
            return userLicenseeIds[0];
        }
        return null; // null = "All licensees"
    };

    const [currentLicensee, _setCurrentLicensee] = useState(() => {
        if (!isFranchise) return null;
        const initial = getInitialLicensee();
        // Sync to sharedContext so withFranchiseContext can read it immediately
        if (initial != null) {
            sharedContext.set('selected_licensee_id', initial);
        }
        return initial;
    });

    const setCurrentLicensee = (licenseeId) => {
        _setCurrentLicensee(licenseeId);
        if (licenseeId != null) {
            sharedContext.set('selected_licensee_id', licenseeId);
        } else {
            sharedContext.delete('selected_licensee_id');
        }
    };

    const countryMap = bootstrap?.countries && bootstrap.countries.length > 0 ? list2map(bootstrap.countries, 'id') : {};
    const currencyMap = bootstrap?.currencies && bootstrap.currencies.length > 0 ? list2map(bootstrap.currencies, 'id') : {};
    const roleMap = bootstrap?.roles && bootstrap.roles.length > 0 ? list2map(bootstrap.roles, 'id') : {};

    // CP-8056: sharedContext (localStorage) stays the source of truth for bootstrap
    // data, but writes must also trigger a React re-render so hasPerm consumers
    // (menu, feature gates) pick up permission changes without a page reload.
    const [, setBootstrapVersion] = useState(0);

    // Logout in one tab wipes localStorage for every tab, but the others kept running on
    // their in-memory token while code reading localStorage directly (chat_utils) sent
    // user_id "undefined" (CPBACKEND-EXYP). Follow the other tab back to the login page.
    const userId = user?.id;
    const tokenRef = useRef(token);
    tokenRef.current = token;
    useEffect(() => {
        const onStorage = (event) => {
            if (isBootstrapSignOutElsewhere(event, userId)) {
                console.log('contextStore: signed out (or switched user) in another tab, returning to login');
                window.location.href = window.location.origin + '/';
            } else if (isSessionTokenRotatedElsewhere(event, userId, tokenRef.current)) {
                // In memory only: localStorage already holds it (the other tab wrote it).
                sharedContext.set('bootstrap', JSON.parse(event.newValue), false);
                setBootstrapVersion((v) => v + 1);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [userId]);

    // Setters must keep a STABLE identity across renders: now that setBootstrapKey
    // re-renders the provider, an unmemoized setter would be a fresh function on
    // every bootstrap write. Consumers that list a setter in useCallback/useEffect
    // deps (e.g. useGeneralSettings' getTourOperatorUpdateData -> updateTourOperator)
    // would then re-fire their fetch effect on each write and spin forever.
    // Safe with empty deps: the body reads bootstrap from sharedContext, not from
    // the render closure.
    const setBootstrapKey = useCallback((key, value) => {
        const bs = sharedContext.get('bootstrap');
        const nv = (typeof value === 'function') ? value(bs[key] ?? {}) : value;
        const nbs = { ...bs, [key]: nv };
        sharedContext.set('bootstrap', nbs, true);
        setBootstrapVersion((v) => v + 1);
    }, []);
    // Swaps the whole session (login, "Log in as") in ONE write and one re-render. Key by
    // key, every write re-rendered the app, and effects running between the writes saw the
    // new user with the previous session's token.
    const replaceBootstrap = useCallback((nbs) => {
        sharedContext.set('bootstrap', nbs, true);
        setBootstrapVersion((v) => v + 1);
    }, []);
    // The Sign-in security page's confirms sign the session in again (dashboard_mfa.openapi.yaml
    // DashboardMfaSessionRotated): the backend deletes the old token, so the new one replaces it in one write --
    // no sign-out, and the data provider (App.js reads the token through a ref) sends it from the next request.
    const replaceSessionToken = useCallback(({ token: nextToken, token_expires: nextExpires }) => {
        const bs = sharedContext.get('bootstrap');
        if (!bs || !nextToken) return;
        replaceBootstrap({ ...bs, token: nextToken, token_expires: nextExpires || bs.token_expires });
    }, [replaceBootstrap]);
    // bootstrap.mfa, the user menu's "Sign-in security" (null: the backend's feature flag is off). Written at
    // sign-in, refreshed by layout/main.js once per page load and by the page itself. One write, only on a change.
    const setMfaStatus = useCallback((status) => {
        const bs = sharedContext.get('bootstrap');
        const next = status || null;
        if (!bs || JSON.stringify(bs.mfa ?? null) === JSON.stringify(next)) return;
        replaceBootstrap({ ...bs, mfa: next });
    }, [replaceBootstrap]);
    const setUser = useCallback((v) => setBootstrapKey('user', v), [setBootstrapKey]);
    const setTourOperator = useCallback((v) => setBootstrapKey('tour_operator', v), [setBootstrapKey]);
    const setToken = useCallback((v) => setBootstrapKey('token', v), [setBootstrapKey]);
    const setTokenExpires = useCallback((v) => setBootstrapKey('token_expires', v), [setBootstrapKey]);
    // Written at login and refreshed by the my_permissions poll (layout/main.js), so a
    // licensee created or granted mid-session shows up without a re-login.
    const setFranchiseData = useCallback((v) => setBootstrapKey('franchise', v), [setBootstrapKey]);
    // Written at login (from /bootstrap/) and refreshed by the Developer Overview, so a session
    // stored before the backend sent `environment` corrects itself. One write, only on a change.
    const setDeveloperEnvironment = useCallback((fields) => {
        const { environment: env, production_key_banner: banner } = fields;
        const bs = sharedContext.get('bootstrap');
        if (!bs || !env) return;
        const nextBanner = banner || [];
        // `paired` (DSEC D3) only when the caller has it: the Overview and the bootstrap do.
        const nextPaired = 'paired' in fields ? (fields.paired || null) : (bs.paired ?? null);
        if (
            bs.environment === env
            && JSON.stringify(bs.production_key_banner || []) === JSON.stringify(nextBanner)
            && JSON.stringify(bs.paired ?? null) === JSON.stringify(nextPaired)
        ) return;
        replaceBootstrap({ ...bs, environment: env, production_key_banner: nextBanner, paired: nextPaired });
    }, [replaceBootstrap]);

    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [catColor, setCatColor] = useState({});

    const tagColor = (tag) => {
        const cat = (tag.category && tag.category.code ? tag.category.code : tag.category).toString().trim();
        // console.log('tagColor', tag, cat, catColor);
        if (!catColor[cat]) {
            const color = catColor[cat] = colorList[Object.keys(catColor).length % colorList.length];
            setCatColor((prev) => ({ ...prev, [cat]: color }));
            return color;
        }
        return catColor[tag.category.code];
    }

    const checkIsAuthenticated = () => {
        // console.log('checkIsAuthenticated:', !!bootstrap, !!bootstrap?.token, !!bootstrap?.user, !!bootstrap?.tour_operator, !!bootstrap?.settings);
        if (!isAuthenticated) {
            console.log('checkIsAuthenticated: isAuthenticated is false');
            return false;
        }
        const expires = tokenExpires;
        if (!expires) {
            console.log('checkIsAuthenticated: no token expiration');
            return false;
        }
        const now = moment();
        const exp = moment(expires);
        console.log('checkIsAuthenticated: user is authenticated if:', exp.diff(now), ' > 0');
        return exp.diff(now) > 0;
    };

    const setupLoggedInUser = async (data, bsdata) => {
        // console.log('setupLoggedInUser:', data, bsdata);
        // One write (see replaceBootstrap). The per-key setters used to run first: on "Log in
        // as", the my_permissions poll fired between setUser and setToken with the
        // impersonator's token and stored the impersonator's permissions on the new user; the
        // menu then requested pages that user may not see, and the first 403 signed them out.
        replaceBootstrap({
            user: data.user,
            auth_method: data.auth_method,
            tour_operator: data.tour_operator,
            settings: data.settings,
            token: data.token,
            token_expires: data.token_expires,
            countries: bsdata.countries,
            currencies: bsdata.currencies,
            roles: bsdata.roles,
            timeline_types: bsdata.timeline_types,
            franchise: bsdata.franchise || null,
            environment: bsdata.environment || null,
            production_key_banner: bsdata.production_key_banner || [],
            paired: bsdata.paired || null,
            // Two-step sign-in (dashboard_mfa.openapi.yaml DashboardMfaStatus): absent while the backend's
            // feature flag is off, and then the user menu has no "Sign-in security".
            mfa: bsdata.mfa || null,
        });
    }

    const finalizeLogin = async (data, path_after) => {
        // console.log('finalizeLogin:', data);
        const bootstrapUrl = `${config.apiUrl}/bootstrap/`;
        let bs_res = await fetch(bootstrapUrl, {
            cache: 'no-cache',
            headers: {
                Authorization: `Token ${data.token}`,
            },
        });

        let bsdata = await bs_res.json();

        await setupLoggedInUser(data, bsdata);
        
        if (path_after) {
            if (path_after === '/') {
                window.location.href = window.location.origin + '/';
            } else {
                window.location.href = path_after;
            }
        } else {
            window.location.reload();
        }
    }

    // The password step of the sign-in (apiv2/dashboard_auth.openapi.yaml, POST /v2/edit/login/). Resolves to
    // the sign-in page's next step: `{kind: 'mfa', pending}` or `{kind: 'enroll', enrollment}` for a 202 (two-step
    // sign-in, auth/MfaCodeStep / MfaEnrollStep), null otherwise -- signed in (a 201 goes on to finalizeLogin
    // and `intendedPath`, as before) or refused, with `errorMessage` set. The body is read on every answer, and a
    // network error is a message too: the form never stays on "loading".
    const login = async (loginInfo, intendedPath = null) => {
        setLoading(true);
        setError(null);
        setErrorMessage(null);
        const refuse = (message) => {
            setError(true);
            setErrorMessage(message);
            setLoading(false);
            return null;
        };

        const options = {
            method: 'POST',
            cache: 'no-cache',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                username: loginInfo.username,
                password: loginInfo.password,
                host: config.domainOverride || window.location.host,
            }),
        };
        const authUrl = `${config.apiUrl}/login/`;

        let login_res;
        let data = null;
        try {
            login_res = await fetch(authUrl, options);
            // 400 / 404 answer an empty body.
            data = await login_res.json().catch(() => null);
        } catch (err) {
            return refuse(SIGN_IN_UNREACHABLE);
        }
        const status = login_res.status;
        if ((status === 200 || status === 201) && data) {
            try {
                await finalizeLogin(data, intendedPath);
            } catch (err) {
                return refuse(SIGN_IN_UNREACHABLE);
            }
            return null;
        }
        if (status === 202 && data?.mfa_required) {
            setLoading(false);
            return { kind: 'mfa', pending: data };
        }
        if (status === 202 && data?.mfa_enrollment_required) {
            setLoading(false);
            return { kind: 'enroll', enrollment: data };
        }
        if (status === 403 && data?.code === 'password_sign_in_disabled') {
            return refuse(data.detail || 'Your company signs in with single sign-on.');
        }
        // A closed tenant (or a licensee of a closed franchise parent): refused before the password is checked, and
        // final -- the backend's neutral words, nothing to retry.
        if (status === 403 && data?.code === 'tenant_closed') {
            return refuse(data.detail || TENANT_CLOSED_TEXT);
        }
        if (status === 429) {
            const retryAfter = Number.isInteger(data?.retry_after) ? data.retry_after : parseInt(login_res.headers?.get?.('Retry-After'), 10);
            return refuse(tooManyAttempts(retryAfter));
        }
        if (status >= 500) {
            return refuse('The server could not sign you in right now. Try again in a few minutes.');
        }
        return refuse(SIGN_IN_FAILED);
    };

    // `returnPath`: an app path to land on afterwards (the MCP sign-in handoff signs a stale session
    // out and comes back to itself); the sign-in page otherwise. Callers that pass nothing — or an
    // event — get the root as before.
    // Resolves once the page is navigating away (callers such as signOut() wait for it before their own redirect).
    const logout = async (returnPath) => {
        if (_refreshTm) {
            clearTimeout(_refreshTm);
        }

        // Read from the bootstrap deleted just below, so a second logout() (two 401s at once) posts nothing; it
        // waits for the first one's request instead of navigating it away.
        const sessionToken = sharedContext.get('bootstrap')?.token;
        if (sessionToken) {
            _revoking = revokeSessionToken(sessionToken);
        }
        const revoking = _revoking;

        sharedContext.delete('bootstrap');
        sharedContext.delete('selected_licensee_id');
        sharedContext.delete('twilioClientIsInitializing');
        sharedContext.delete('twilioClient');
        sharedContext.delete('unreadMsgCount');

        sharedContext.clear(); // added by Jordan 9/27/2022 to ensure all chat related keys are deleted as well - is this OK? Doesn't seem to actually clear anything

        if (window.navigator && navigator.serviceWorker) {
            navigator.serviceWorker.getRegistrations().then(function (registrations) {
                for (let registration of registrations) {
                    registration.unregister();
                }
            });
        }

        await revoking;

        // Redirect to root (or returnPath) before reload to reset URL
        const isAppPath = typeof returnPath === 'string' && returnPath.startsWith('/') && !returnPath.startsWith('//');
        window.location.href = window.location.origin + (isAppPath ? returnPath : '/');

        // setIsAuthenticated(false);

        // return state;
        return;
    };

    // Stable identity — see the note on setBootstrapKey above. Settings pages list
    // this in their fetch-effect deps.
    // Only the session's own operator is cached here. A settings page that passed on a
    // refused save's body (a 406 {message}, a 404 page's []) cached it as the operator: the
    // id was gone, the next load fetched the operator LIST, and every later save went to
    // /tour_operator/undefined/ until the user logged in again.
    const updateTourOperator = useCallback((data) => {
        const currentId = sharedContext.get('bootstrap')?.tour_operator?.id;
        if (!data || !Number.isInteger(data.id) || data.id !== currentId) {
            console.error('updateTourOperator: not caching', data?.id, 'as operator', currentId);
            Sentry.captureMessage('updateTourOperator refused a payload that is not the session operator', 'warning');
            return;
        }
        setTourOperator(data);
    }, [setTourOperator]);

    const checkAuth = () => {
        throw new Error('checkAuth is obsolete and should not be used');
    };

    const rotateToken = async (token) => {
        if (!token) {
            return;
        }
        const options = {
            method: 'POST',
            cache: 'no-cache',
            headers: { Authorization: `Token ${token}` },
        };
        const url = `${config.apiUrl}/token/rotate/`;
        const res = await fetch(url, options);

        try {
            if ([200, 201].indexOf(res.status) >= 0) {
                let res_data = await res.json();
                setToken(res_data.token);
                setTokenExpires(res_data.token_expires);
                return res_data;
            } else {
                console.error(`rotateToken: current token is invalid or expired`);
                // setIsAuthenticated(false);
                setToken(null);
                setTokenExpires(null);

                sharedContext.delete('bootstrap');
                return new Error(`${res.status} ${res.statusText}`);
            }
        } catch (err) {
            console.error(`rotateToken: error parsing server response: ${err}`);
            sharedContext.delete('bootstrap');
            return err;
        }
    };

    const tokenRefreshDelay = () => {
        const expires = tokenExpires;
        if (!expires) {
            return 1000; // refresh token in a second if token expiration time is unknown
        }
        const now = moment();
        const exp = moment(expires);
        const refreshTime = exp.subtract(90, 'seconds');
        const delay = refreshTime.diff(now);
        if (!delay || delay <= 0) {
            return 30000;
        } else {
            return delay;
        }
    };

    const hasPerm = (scope, action) => {
        try {
            //console.log(`audit: hasPerm: user ${user.email}: checking ${scope}.${action}`, user.permissions);
            if (!user.permissions) {
                //console.log(`audit: hasPerm: user ${user.email}: ${scope}.${action} access: denied because permissions failed to load`, user.permissions);
                return true;
            }
            const up = user.permissions ?? {};
            if (!up.access_groups_enabled) {
                //console.log(`audit: hasPerm: user ${user.email}: granting ${scope}.${action} permission because access groups are not enabled`);
                return true;
            }
            if (up.full_access) {
                //console.log(`audit: hasPerm: user ${user.email}: granting ${scope}.${action} permission because user has full access`);
                return true;
            }
            if (up.scoped_all_tags && up.scoped_all_tags[scope] && up.scoped_all_tags[scope][action]) {
                //console.log(`audit: hasPerm: user ${user.email}: granting ${scope}.${action} permission because user has access to all tags`);
                return true;
            }
            if (up.perms && up.perms[scope] && up.perms[scope][action]) {
                //console.log(`audit: hasPerm: user ${user.email}: granting ${scope}.${action} permission because user has access to some tags (${up.perms[scope][action]})`);
                return true;
            }
            //console.log(`audit: hasPerm: user ${user.email}: ${scope}.${action} access: denied by default`);
            return false;
        } catch (e) {
            console.log('audit: hasPerm: error', e);
            return false;
        }
    };

    // Restriction permissions invert the usual grant model: the user is
    // restricted only when the permission is *explicitly* granted to one of
    // their access groups. Unlike hasPerm, this must NOT default to true when
    // access groups are disabled or the user has full access — otherwise every
    // non-RBAC operator would be treated as restricted.
    const hasRestriction = (scope, action) => {
        const up = user?.permissions;
        if (!up) return false;
        if (!up.access_groups_enabled) return false;
        if (up.full_access) return false;
        return !!(up.scoped_all_tags?.[scope]?.[action] || up.perms?.[scope]?.[action]);
    };

    // An explicitly granted permission (backend `ag_has_explicit_perm`): "Full access" does NOT imply it,
    // and it is not implied when the operator has access groups disabled either -- unlike hasPerm. For
    // explicit-only permissions such as notification.mcp_read / mcp_write (AI editing over MCP, metaplan
    // A8/A10): a page or menu item gated on hasPerm would show for full-access admins and then get a 403.
    const hasExplicitPerm = (scope, action) => {
        const up = user?.permissions;
        if (!up) return false;
        return !!(up.scoped_all_tags?.[scope]?.[action] || up.perms?.[scope]?.[action]);
    };

    const accessGroupsEnabled = () => (user?.permissions?.access_groups_enabled);

    const hasAllPerms = (list) => {
        const parts = list.map((item, idx, lst) => {
            let scope, action;
            if (item.constructor === Array && item.length === 2) {
                scope = item[0];
                action = item[1];
            } else if (item.constructor === String && item.indexOf('.') > 0) {
                [scope, action] = item.split('.', 2);
            } else {
                throw Error("hasAllPerms: malformed permission specifier: " + item.toString());
            }
            // console.log('hasAllPerms', item, '|', scope, action, hasPerm(scope, action));
            return hasPerm(scope, action)
        });
        // console.log('hasAllPerms', list, parts, parts.reduce((acc, x) => (acc && x), true));
        return parts.reduce((acc, x) => (acc && x), true);
    }

    const hasTagPerm = (scope, action, atag) => {
        try {
            // console.log(`audit: hasTagPerm: user ${user.email}: checking ${scope}.${action}`, user.permissions);
            if (!!user.permissions) {
                //console.log(`audit: hasTagPerm: user ${user.email}: ${scope}.${action} access: denied because permissions failed to load`);
                return false;
            }
            const up = user.permissions ?? {};
            if (!up.access_groups_enabled) {
                //console.log(`audit: hasTagPerm: user ${user.email}: granting ${scope}.${action} access because access groups are not enabled`);
                return true;
            }
            if (up.full_access) {
                //console.log(`audit: hasTagPerm: user ${user.email}: granting ${scope}.${action} access because user has full access`);
                return true;
            }
            if (up.scoped_all_tags && up.scoped_all_tags[scope] && up.scoped_all_tags[scope][action]) {
                //console.log(`audit: hasTagPerm: user ${user.email}: granting ${scope}.${action} access because user has access to all tags`);
                return true;
            }
            if (up.perms && up.perms[scope] && up.perms[scope][action]) {
                let grant = false;
                if (atag.constructor === Array) {
                    grant = atag.every((id) => up.perms[scope][action].includes(id));
                } else {
                    grant = up.perms[scope][action].includes(+atag);
                }
                //console.log(`audit: hasTagPerm: user ${user.email}: ${scope}.${action} ${grant ? 'granting' : 'denying'} access because access tags don't match (req=${atag}, user=${up.perms[scope][action]})`);
                return grant;
            }
            //console.log(`audit: hasTagPerm: user ${user.email}: denying ${scope}.${action} access by default`);
            return false;
        } catch (e) {
            console.log(`audit: hasTagPerm: user ${user.email}: error`, e);
            return false;
        }
    };

    // Franchise: may this user set all_tags=True on a permission scope? Backend is the
    // source of truth (my_permissions.can_grant_all_tags); absent field (non-franchise
    // TO or older backend) means unrestricted.
    const canGrantAllTags = () => {
        const up = user?.permissions;
        if (!up) return true;
        return up.can_grant_all_tags !== false;
    };

    // Franchise: tags this user may grant to access groups. null = unrestricted;
    // an array = restricted to these ids (my_permissions.grantable_atag_ids).
    const grantableAtagIds = () => {
        const ids = user?.permissions?.grantable_atag_ids;
        return Array.isArray(ids) ? ids : null;
    };

    const hasPermForLicensee = (scope, action, licenseeId) => {
        try {
            if (!hasPerm(scope, action)) return false;
            const up = user?.permissions ?? {};
            if (!up.access_groups_enabled || up.full_access) return true;
            if (!up.per_licensee || !licenseeId) return true;
            const lp = up.per_licensee[String(licenseeId)];
            if (!lp) return false;
            if (lp.full_access) return true;
            if (lp.scoped_all_tags?.[scope]?.[action]) return true;
            if (lp.perms?.[scope]?.[action]) return true;
            return false;
        } catch (e) {
            return false;
        }
    };

    const isCuratedPlanetUser = user?.email?.toString().endsWith('@curatedplanet.com') || user?.email?.toString().endsWith('@touroptima.com');
    const isAWSUser = ['vladimir@curatedplanet.com', 'jordan@curatedplanet.com', 'ben@curatedplanet.com'].indexOf(user?.email?.toString().toLowerCase()) >= 0;

    return (
        <ContextStore.Provider
            value={{
                login,
                logout,
                finalizeLogin,
                setupLoggedInUser,
                checkAuth,
                rotateToken,
                accessGroupsEnabled,
                hasPerm,
                hasRestriction,
                hasExplicitPerm,
                hasAllPerms,
                hasTagPerm,
                hasPermForLicensee,
                canGrantAllTags,
                grantableAtagIds,
                bootstrap,
                user,
                auth_method,
                setUser,
                token,
                tourOperator,
                settings,
                countries,
                currencies,
                roles,
                timelineTypes,
                loading,
                error,
                errorMessage,
                updateTourOperator,
                roleMap,
                countryMap,
                currencyMap,
                isAuthenticated,
                tokenExpires,
                tokenRefreshDelay,
                checkIsAuthenticated,
                tagColor,
                colorList,
                isCuratedPlanetUser,
                isAWSUser,
                isFranchise,
                licensees,
                userLicenseeIds,
                currentLicensee,
                setCurrentLicensee,
                franchiseData,
                setFranchiseData,
                environment,
                productionKeyBanner,
                pairedEnvironment,
                setDeveloperEnvironment,
                mfaStatus: bootstrap?.mfa || null,
                setMfaStatus,
                replaceSessionToken,
            }}
        >
            {children}
        </ContextStore.Provider>
    );
};
